import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { APP_STORE_KEY } from '@/constants/storage';

// Define the store state type
interface AppState {
  // Only populated after the current browser session completes backend login.
  registrationRequired: boolean;
  setRegistrationRequired: (required: boolean) => void;
  backendUserId: string | null;
  setBackendUserId: (id: string | null) => void;

  // User related state
  user: {
    isAuthenticated: boolean;
    name: string | null;
    username: string | null;
    email: string | null;
    email_verified: boolean | null;
    image: string | null;
    role: string | null;
    // Backend user data (from LoginResponse)
    id: string | null;
    roles: string[] | null;
    [key: string]: unknown;
  };


  // Session management for idle timeout (handled by useIdle hook)
  session: {
    isValid: boolean;
    isLoggingOut: boolean;
  };

  // UI related state
  ui: {
    darkMode: boolean;
    sidebarOpen: boolean;
  };

  // Actions
  setUser: (user: Partial<AppState['user']>) => void;
  setLoggingOut: () => void;
  clearLoggingOutFlag: () => void;
  logout: () => void;
  toggleDarkMode: () => void;
  setSidebarOpen: (open: boolean) => void;
}

// Create the store
const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      registrationRequired: false,
      setRegistrationRequired: (required) => set({ registrationRequired: required }),
      backendUserId: null,
      setBackendUserId: (id) => set({ backendUserId: id }),

      // Initial state
      user: {
        isAuthenticated: false,
        name: null,
        username: null,
        email: null,
        email_verified: null,
        image: null,
        role: null,
        id: null,
        roles: null,
      },
      session: {
        isValid: true,
        isLoggingOut: false,
      },
      ui: {
        darkMode: false,
        sidebarOpen: true,
      },

      // Actions
      setUser: (userData) =>
        set((state) => ({
          user: {
            ...state.user,
            ...userData,
            isAuthenticated: true,
          },
          session: {
            ...state.session,
            isValid: true,
            isLoggingOut: false,
          },
        })),

      setLoggingOut: () =>
        set((state) => ({
          session: {
            ...state.session,
            isLoggingOut: true,
          },
        })),

      clearLoggingOutFlag: () =>
        set((state) => ({
          session: {
            ...state.session,
            isLoggingOut: false,
          },
        })),

      logout: () =>
        set(() => ({
          registrationRequired: false,
          backendUserId: null,
          user: {
            isAuthenticated: false,
            name: null,
            username: null,
            email: null,
            email_verified: null,
            image: null,
            role: null,
            id: null,
            roles: null,
          },
          session: {
            isValid: false,
            isLoggingOut: false,
          },
        })),

      toggleDarkMode: () =>
        set((state) => ({
          ui: {
            ...state.ui,
            darkMode: !state.ui.darkMode,
          },
        })),

      setSidebarOpen: (open: boolean) =>
        set((state) => ({
          ui: {
            ...state.ui,
            sidebarOpen: open,
          },
        })),
    }),
    {
      name: APP_STORE_KEY, // unique name for sessionStorage
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        ui: { darkMode: state.ui.darkMode },
      }) as Partial<AppState>, // persist user, session, and darkMode preference in session
    },
  ),
);

export default useAppStore;
